import { useRef } from 'react';

import { Box, Link, List, Typography, paletteColors } from '@evaneos/b2b-ui';
import { useIntl } from 'react-intl';
import { differenceInHours } from 'date-fns';

import WarningIcon from '@evaneos/b2b-ui/icons/WarningAmber';

import { Block } from '../shared/Block';

import { DisplayDossierOverviews } from '../../../resources/dossier/types';

import { MarkEmailRead } from '../../../design-system/Icons/MarkEmailRead';
import { useTrackEventAmplitude } from '../../../shared/analytics';

import { MessageItem } from './MessageItem';

function scrollMessageItemIntoList(
    listContainer: HTMLUListElement,
    listItemLink: HTMLAnchorElement,
) {
    const containerTop = listContainer.scrollTop;
    const containerBottom = containerTop + listContainer.clientHeight;

    const listItem = listItemLink.closest('li');
    if (!listItem) {
        return;
    }

    const listItemTop = listItem.offsetTop;
    const listItemBottom = listItem.offsetTop + listItem.offsetHeight;

    if (listItemTop < containerTop) {
        listContainer.scrollTop = listItemTop;
    } else if (listItemBottom > containerBottom) {
        listContainer.scrollTop = listItemBottom - listContainer.clientHeight;
    }
}

export const MessageListBlock = ({
    responseRequiredDossiers,
}: {
    responseRequiredDossiers: DisplayDossierOverviews;
}) => {
    const { formatMessage } = useIntl();
    const { trackEvent } = useTrackEventAmplitude();
    const firstResponseOverdueDossierRef = useRef<HTMLAnchorElement | null>(null);
    const listRef = useRef<HTMLUListElement | null>(null);

    const hasDossiers = responseRequiredDossiers.length > 0;

    const dossiersWithResponseOverdue = responseRequiredDossiers.filter((dossier) => {
        const { lastTravellerToAgentMessageDate } = dossier.discussion;

        if (!lastTravellerToAgentMessageDate) {
            return false;
        }

        const hoursSinceLastTravellerMessage = differenceInHours(
            new Date(),
            lastTravellerToAgentMessageDate,
        );

        return hoursSinceLastTravellerMessage > 48;
    });
    const responseOverdueDossierIds = new Set(dossiersWithResponseOverdue.map((d) => d.id));

    const handleRevealFirstResponseOverdueDossier = () => {
        if (dossiersWithResponseOverdue.length > 0) {
            const [first] = dossiersWithResponseOverdue;

            trackEvent((ampli, defaultProperties) => {
                ampli.overdueMessageClicked({
                    ...defaultProperties,
                    ets_source: 'request_manager',
                    request_id: parseInt(first.id),
                    message_id: first.discussion.lastTravellerUnansweredMessageId ?? undefined,
                });
            });
        }
        if (firstResponseOverdueDossierRef.current && listRef.current) {
            scrollMessageItemIntoList(listRef.current, firstResponseOverdueDossierRef.current);
            firstResponseOverdueDossierRef.current?.focus();
        }
    };

    return (
        <Block
            title={formatMessage(
                { id: 'homepage.response_required.title' },
                {
                    count: responseRequiredDossiers.length,
                },
            )}
            headerContent={
                dossiersWithResponseOverdue.length > 0 && (
                    <Typography
                        color={paletteColors.red[800]}
                        fontWeight="bold"
                        variant="body2"
                        display="flex"
                        alignItems="center"
                        gap={0.5}
                        flexWrap="wrap"
                        mt={0.5}
                    >
                        <Box component="span" display="flex" alignItems="center" gap={0.5}>
                            <WarningIcon
                                htmlColor={paletteColors.red[800]}
                                fontSize="small"
                                aria-hidden={undefined}
                                aria-label={formatMessage({
                                    id: 'homepage.response_required.warning.icon_label',
                                })}
                            />
                            {formatMessage(
                                { id: 'homepage.response_required.warning.label' },
                                { count: dossiersWithResponseOverdue.length },
                            )}
                        </Box>
                        <Link
                            component="button"
                            sx={{
                                fontWeight: 'bold',
                                fontFamily: 'inherit',
                            }}
                            onClick={handleRevealFirstResponseOverdueDossier}
                        >
                            {formatMessage({
                                id: 'homepage.response_required.warning.see_messages',
                            })}
                        </Link>
                    </Typography>
                )
            }
        >
            {hasDossiers ? (
                <List
                    data-testid="response-required-messages-list"
                    ref={listRef}
                    disablePadding
                    sx={{
                        flex: { xs: '1 1 336px', md: '1 1 0' },
                        overflowY: 'auto',
                    }}
                >
                    {responseRequiredDossiers.map((dossier) => {
                        const isFirstResponseOverdueDossier =
                            dossiersWithResponseOverdue[0]?.id === dossier.id;
                        const isResponseOverdue =
                            isFirstResponseOverdueDossier ||
                            responseOverdueDossierIds.has(dossier.id);

                        return (
                            <MessageItem
                                key={dossier.id}
                                dossier={dossier}
                                ref={
                                    isFirstResponseOverdueDossier
                                        ? firstResponseOverdueDossierRef
                                        : undefined
                                }
                                isResponseOverdue={isResponseOverdue}
                            />
                        );
                    })}
                </List>
            ) : (
                <Box
                    data-testid="empty-response-required-messages"
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flex: { xs: '0 0 auto', md: 1 },
                        alignItems: 'center',
                        justifyContent: 'center',
                        px: 3,
                        margin: 'auto',
                        textAlign: 'center',
                        minHeight: { xs: '200px', md: 'auto' },
                    }}
                >
                    <MarkEmailRead
                        color={paletteColors.green[700]}
                        aria-hidden="true"
                        width={32}
                        height={32}
                        style={{ marginBottom: '0.5rem' }}
                    />
                    <Typography variant="body1" fontWeight={700}>
                        {formatMessage({ id: 'homepage.response_required.empty.title' })}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" fontWeight={700}>
                        {formatMessage({
                            id: 'homepage.response_required.empty.description',
                        })}
                    </Typography>
                </Box>
            )}
        </Block>
    );
};
